import React from "react";
import { withTenantContextInHtml, type AttachmentView, type MessageView } from "@/api";
import { FACT_TONE, Icon, cx, fmtStamp } from "@/lib";
import { useT } from "../../i18n";
import { activeJoins, marksForMessage, segmentHasMark, segmentMarkKeys, segmentText, type EvidenceMark } from "../evidence-highlight";
import { AttachmentStrip } from "./attachment-strip";
import { messageDisplayBlocks, quoteLineLevel, type DisplayBlock } from "./quote-display";
import styles from "./email-review.module.css";

/** Stable default so the display-blocks memo does not recompute for prop omission. */
const NO_BODIES: string[] = [];
const NO_ATTACHMENTS: AttachmentView[] = [];

function HighlightedMessageText({
  body,
  marks,
  active,
  onHover,
}: {
  body: string;
  marks: EvidenceMark[];
  active: string | null;
  onHover: (key: string | null) => void;
}) {
  const segments = React.useMemo(() => segmentText(body, marks), [body, marks]);
  return segments.map((segment, index) => {
    if (!segment.markKey) return <React.Fragment key={index}>{segment.text}</React.Fragment>;
    const tone = FACT_TONE[segment.status ?? ""] ?? "neutral";
    return (
      <mark
        key={index}
        className={cx(styles.mark, styles[`mark_${tone}`], segmentHasMark(segment, active) && styles.markActive)}
        data-mark={segment.markKey}
        data-marks={segmentMarkKeys(segment)}
        data-joins={activeJoins(segments, index, active)}
        onMouseEnter={() => onHover(segment.markKey!)}
        onMouseLeave={() => onHover(null)}
      >
        {segment.text}
      </mark>
    );
  });
}

/** Quoted or repeated history rendered only after the full-message disclosure opens. */
function HistoryBlock({
  block,
  marks,
  active,
  onHover,
}: {
  block: DisplayBlock;
  marks: EvidenceMark[];
  active: string | null;
  onHover: (key: string | null) => void;
}) {
  const lines = block.text.split("\n");
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return (
    <div
      className={styles.historyBlock}
      data-testid="history-block"
      data-kind={block.kind}
    >
      <div className={styles.historyContent}>
        {lines.map((line, index) => {
          const level = quoteLineLevel(line);
          return (
            <div
              key={index}
              className={styles.historyLine}
              data-level={level > 0 ? Math.min(level, 4) : undefined}
            >
              {line === ""
                ? " "
                : <HighlightedMessageText body={line} marks={marks} active={active} onHover={onHover} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface HistoryVisibility {
  messageKey: string | null;
  open: boolean;
}

/**
 * Which rendering of the message body is on screen (#594). The plain-text view is the default
 * because it is the one that carries evidence highlighting and the quoted-history disclosure;
 * the sender's own formatting is one deliberate click away.
 */
type BodyView = "text" | "formatted";

interface BodyViewChoice {
  messageKey: string | null;
  view: BodyView;
}

export function SelectedMessagePane({
  requestId,
  message,
  messageKey,
  unread,
  marks,
  activeMark,
  onMarkHover,
  navigation,
  actions,
  earlierBodies = NO_BODIES,
}: {
  /** Owns the attachment URLs — every one is fetched through its request (#594). */
  requestId: string;
  message: MessageView | null;
  messageKey: string | null;
  unread: boolean;
  marks: EvidenceMark[];
  activeMark: string | null;
  onMarkHover: (key: string | null) => void;
  navigation?: React.ReactNode;
  actions?: React.ReactNode;
  /** Bodies of the thread's earlier messages, for repeated-history detection. */
  earlierBodies?: string[];
}) {
  const t = useT();
  const unreadDescriptionId = React.useId();
  const inbound = message?.direction === "inbound";
  const blocks = React.useMemo(
    () => (inbound ? messageDisplayBlocks(message?.normalizedText || "", earlierBodies) : []),
    [inbound, message?.normalizedText, earlierBodies],
  );
  const messageMarks = inbound && message ? marksForMessage(marks, message.id) : [];
  const currentMessageKey = messageKey ?? message?.id ?? null;
  const firstHistoryIndex = blocks.findIndex((block) => block.kind !== "new");
  const activeMarkVisibleInNew = React.useMemo(
    () =>
      activeMark !== null
      && blocks.some(
        (block) =>
          block.kind === "new"
          && segmentText(block.text, messageMarks).some((segment) => segmentHasMark(segment, activeMark)),
      ),
    [blocks, messageMarks, activeMark],
  );
  const activeMarkInHistory = React.useMemo(
    () =>
      activeMark !== null
      && blocks.some(
        (block) =>
          block.kind !== "new"
          && segmentText(block.text, messageMarks).some((segment) => segmentHasMark(segment, activeMark)),
      ),
    [blocks, messageMarks, activeMark],
  );
  // An owner browsing another tenant cannot send a header with an image-element load, so the inline
  // sources the server issued get that context as a query parameter instead (#231/#594).
  const bodyHtml = React.useMemo(
    () => (message?.bodyHtml ? withTenantContextInHtml(message.bodyHtml) : null),
    [message?.bodyHtml],
  );
  const formattedAvailable = Boolean(bodyHtml);
  const [bodyViewChoice, setBodyViewChoice] = React.useState<BodyViewChoice>({
    messageKey: null,
    view: "text",
  });
  const formatted =
    formattedAvailable
    && bodyViewChoice.messageKey === currentMessageKey
    && bodyViewChoice.view === "formatted";

  // Evidence marks are offsets into the plain text, so a highlight can only be shown there.
  // Activating one switches the body back — once, and staying, exactly like the history
  // disclosure: a view that flipped back on every mouse-leave would be unreadable.
  React.useEffect(() => {
    if (activeMark === null || currentMessageKey === null) return;
    setBodyViewChoice((current) =>
      current.messageKey === currentMessageKey && current.view === "text"
        ? current
        : { messageKey: currentMessageKey, view: "text" });
  }, [activeMark, currentMessageKey]);

  const [historyVisibility, setHistoryVisibility] = React.useState<HistoryVisibility>({
    messageKey: null,
    open: false,
  });
  const historyOpen =
    historyVisibility.messageKey === currentMessageKey && historyVisibility.open;

  // Expand before the caller's post-paint scroll query when evidence exists only in
  // hidden history. A different selected message still starts collapsed by default.
  React.useLayoutEffect(() => {
    if (!activeMarkInHistory || activeMarkVisibleInNew || currentMessageKey === null) return;
    setHistoryVisibility((current) =>
      current.messageKey === currentMessageKey && current.open
        ? current
        : { messageKey: currentMessageKey, open: true });
  }, [activeMarkInHistory, activeMarkVisibleInNew, currentMessageKey]);

  if (!message) {
    return (
      <section className={styles.messagePane}>
        {(navigation || actions) && <header className={cx(styles.messageHeader, styles.selectedMessageHeader)} data-testid="selected-pane-header">{navigation}{actions}</header>}
        <p className={styles.empty}>{t("ws.noMessages")}</p>
      </section>
    );
  }
  const recipients = message.toAddrs?.filter(Boolean) ?? [];
  const displaySubject = message.subject || t("common.noSubject");
  const directionLabel = t(inbound ? "ws.review.inbound" : "ws.review.outbound");
  const sender = message.fromAddr || null;
  const forwardedBy = message.forwardedByAddr || null;
  const forwardedStatus = forwardedBy ? message.forwardedOriginStatus ?? null : null;

  return (
    <article
      className={cx(styles.messagePane, unread && styles.messagePaneUnread)}
      data-testid="selected-email-pane"
      data-message-key={messageKey ?? undefined}
      data-unread={unread ? "true" : undefined}
      aria-label={sender ? `${directionLabel} ${sender}` : directionLabel}
      aria-describedby={unread ? unreadDescriptionId : undefined}
    >
      {unread && <span id={unreadDescriptionId} className="sr-only">{t("ws.review.unreadMessageState")}</span>}
      <header className={cx(styles.messageHeader, styles.selectedMessageHeader)} data-testid="selected-pane-header">
        <div className={styles.selectedMessageHeading}>
          <strong className={styles.selectedMessageDirection} data-testid="selected-message-direction">
            {directionLabel}
          </strong>
          {unread && (
            <span className={cx(styles.railStateBadge, styles.railStateBadgeUnread)} data-message-state="unread" aria-hidden="true">
              <Icon name="mail" size={12} />
              {t("ws.review.unread")}
            </span>
          )}
        </div>
        <div className={styles.messageHeaderMeta}>
          {navigation}
          {message.receivedAt && (
            <time className={styles.messageTime} data-testid="selected-message-timestamp">
              {fmtStamp(message.receivedAt)}
            </time>
          )}
          {actions}
        </div>
      </header>
      <div className={cx(styles.emailScroll, styles.selectedMessageContent)}>
        <dl className={styles.messageAddresses} data-testid="selected-message-meta">
          {sender && <>
            <dt data-mail-address-row="from">{t("ws.review.from")}</dt>
            <dd data-mail-address-row="from" data-testid="selected-message-from"><span data-mail-address-content="from">{sender}</span></dd>
          </>}
          {recipients.length > 0 && <>
            <dt data-mail-address-row="to">{t("ws.review.to")}</dt>
            <dd data-mail-address-row="to" data-testid="selected-message-to"><span data-mail-address-content="to">{recipients.join(", ")}</span></dd>
          </>}
        </dl>
        {/* #1385: a colleague forwarded this in. When the forward was read, "From" above is
            already the customer and this names who passed it on; when it was not, "From" is the
            colleague and the sentence says which reason it was — so nobody has to wonder why an
            approved reply is addressed to a coworker. Outside the address grid, which has exactly
            two rows and a column narrow enough for "Von"/"An". */}
        {forwardedBy && (
          <p
            className={styles.forwardedOrigin}
            data-testid="selected-message-forwarded-by"
            data-forwarded-status={forwardedStatus ?? undefined}
          >
            <span className={styles.messageFieldLabel}>{t("ws.review.forwardedBy")}</span>
            <span data-mail-address-content="forwarded-by">{forwardedBy}</span>
            {forwardedStatus && (
              <span className={styles.forwardedNote} data-testid="selected-message-forwarded-note">
                {t(`ws.review.forwarded.${forwardedStatus}`)}
              </span>
            )}
          </p>
        )}
        <h2 className={cx(styles.messageSubject, styles.selectedMessageSubject)} data-testid="selected-message-subject" title={displaySubject}>
          <span className={styles.messageFieldLabel} aria-hidden="true">{t("ws.review.subject")}</span>
          <span className={styles.messageSubjectText}>{displaySubject}</span>
        </h2>
        {formattedAvailable && (
          <div className={styles.bodyViewBar}>
            <button
              type="button"
              className={styles.bodyViewToggle}
              data-testid="body-view-toggle"
              aria-pressed={formatted}
              onClick={() => setBodyViewChoice({
                messageKey: currentMessageKey,
                view: formatted ? "text" : "formatted",
              })}
            >
              {t(formatted ? "ws.body.showPlain" : "ws.body.showFormatted")}
            </button>
          </div>
        )}
        <div className={cx(styles.messageBody, "msg__body")} data-testid="selected-message-body">
          {formatted
            ? (
              <>
                {/* Sanitized server-side on every read (core/src/email/normalization/
                    display-html.ts): no script, no style, no attribute but a link's own target
                    and an image source the server itself issued for one of this message's stored
                    inline attachments. The console never renders a sender's markup unfiltered. */}
                <div
                  className={styles.formattedBody}
                  data-testid="selected-message-formatted"
                  dangerouslySetInnerHTML={{ __html: bodyHtml as string }}
                />
              </>
            )
            : !message.normalizedText
            ? t("ws.review.emptyMessage")
            : inbound
              ? blocks.map((block, index) =>
                  block.kind === "new"
                    ? (
                      <HighlightedMessageText
                        key={`${messageKey ?? ""}:${index}`} body={block.text} marks={messageMarks}
                        active={activeMark} onHover={onMarkHover}
                      />
                    )
                    : (
                      <React.Fragment key={`${messageKey ?? ""}:${index}`}>
                        {index === firstHistoryIndex && (
                          <button
                            type="button"
                            className={styles.historyToggle}
                            data-testid="history-expander"
                            aria-expanded={historyOpen}
                            onClick={() => setHistoryVisibility((current) => ({
                              messageKey: currentMessageKey,
                              open: !(current.messageKey === currentMessageKey && current.open),
                            }))}
                          >
                            {t(historyOpen ? "ws.history.hideFull" : "ws.history.showFull")}
                          </button>
                        )}
                        {historyOpen && (
                          <HistoryBlock
                            block={block}
                            marks={messageMarks}
                            active={activeMark}
                            onHover={onMarkHover}
                          />
                        )}
                      </React.Fragment>
                    ))
              : message.normalizedText}
        </div>
        <AttachmentStrip
          requestId={requestId}
          attachments={message.attachments ?? NO_ATTACHMENTS}
        />
        {(message.bodyImagesRemoved || message.bodyWithheld) && (
          <div className={styles.bodyNotes} data-testid="body-withheld-notes">
            {message.bodyImagesRemoved && (
              <p className={styles.bodyNote} data-testid="body-images-note">
                {t("ws.body.imagesHidden")}
              </p>
            )}
            {message.bodyWithheld === "too_large" && (
              <p className={styles.bodyNote} data-testid="body-too-large-note">
                {t("ws.body.tooLarge")}
              </p>
            )}
            {message.bodyWithheld === "nothing_renderable" && (
              <p className={styles.bodyNote} data-testid="body-not-renderable-note">
                {t("ws.body.notRenderable")}
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
